import type { Box3, Object3D } from "three";

/**
 * Layer the stand-in caster lives on, and nothing else.
 *
 * Layer 0 is everything the scene camera sees, 1 is {@link AO_EXCLUDED_LAYER}
 * and 2 is {@link STUDIO_SHADOW_CASTER_LAYER}. A layer of its own is what makes
 * the swap free: a shadow camera moves to this layer with a single
 * `layers.set()` instead of the scene being re-marked mesh by mesh, and no
 * other camera can see the proxy, so it never reaches the beauty pass or the
 * ambient-occlusion G-buffer.
 */
export const SHADOW_PROXY_LAYER = 3;

/** `userData` marker, so caster marking can skip the proxy. */
export const SHADOW_PROXY_FLAG = "studioShadowProxy";

export function isShadowProxy(object: Object3D): boolean {
  return object.userData[SHADOW_PROXY_FLAG] === true;
}

/** Smallest extent a proxy is given, so a flat product still casts something. */
const MIN_PROXY_EXTENT = 1e-4;

/**
 * Seat a unit box on the product's world bounds.
 *
 * The cast shadow blurs its silhouette into a soft pool — 512 px across — so
 * the geometry that produced it is barely recoverable from the result. A box on
 * the product's bounds is therefore worth close to the product's full mesh set
 * while the case is being resized, at one draw call instead of twenty-six.
 *
 * Only that capture, though: the contact pool keeps the real casters, because
 * drei reuses its camera for the blur quads (see the note in `GroundShadow`).
 * And the cast shadow is itself throttled, so this replaces one capture's draws
 * at the shadow interval, not on every frame of the gesture.
 *
 * Returns false for an empty box, which is the one state where there is
 * nothing to stand in for and the caller must keep the real casters.
 */
export function fitShadowProxy(proxy: Object3D, box: Box3): boolean {
  if (box.isEmpty()) return false;
  proxy.position.set(
    (box.min.x + box.max.x) / 2,
    (box.min.y + box.max.y) / 2,
    (box.min.z + box.max.z) / 2,
  );
  proxy.scale.set(
    Math.max(box.max.x - box.min.x, MIN_PROXY_EXTENT),
    Math.max(box.max.y - box.min.y, MIN_PROXY_EXTENT),
    Math.max(box.max.z - box.min.z, MIN_PROXY_EXTENT),
  );
  proxy.updateMatrixWorld();
  return true;
}

/**
 * Whether a capture should use the proxy rather than the product itself.
 *
 * Only while a gesture is in flight. A resize is the one time the shadow is
 * recaptured over and over — every other capture is a one-off that nobody is
 * waiting on, and there the real silhouette is worth its draw calls. The
 * fallback to the real casters when nothing has been measured matters: the
 * first frames of a canvas publish no bounds, and a proxy fitted to an empty
 * box would cast no shadow at all.
 */
export function shouldUseShadowProxy(input: {
  dragActive: boolean;
  hasBounds: boolean;
}): boolean {
  return input.dragActive && input.hasBounds;
}
